<template>
  <v-form :disabled="form.readonly">
    <v-card>
      <v-card-title>
        <h3>部屋情報の入力</h3>
      </v-card-title>
      <room-table
        :items="form.rooms"
        :floor="step1FormFloors"
        :error-messages="error"
        :readonly="form.readonly"
        :removable="!form.readonly"
        :previous-object="previousObject"
        :created="created"
        :applied="applied"
        @insert="onRoomInserted"
        @update="onRoomUpdated"
        @remove="onRoomRemoved"
        @replace="onRoomReplaced"
        @updateFirst="onCopyRenovationYear1"
        @updateSecond="onCopyRenovationYear2">
      </room-table>
      <v-dialog
        v-model="noRoomsConfirmationIsShown"
        scrollable
        max-width="300px"
        @click:outside="onCloseDialog"
        @close="onCloseDialog">
        <v-card>
          <v-card-title>確認</v-card-title>
          <v-card-text
            class="pt-8 pb-8 pl-5 pl-5 error--text">
            部屋情報が登録されていません。<br />
            次のページに進んでよろしいですか？
          </v-card-text>
          <v-card-actions>
            <v-btn
              color="blue darken-1"
              text
              @click="noRoomsConfirmationIsShown = false">
              キャンセル
            </v-btn>
            <v-spacer />
            <v-btn
              color="primary"
              @click="onConfirmationOkClicked">
              次へ
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <global-footer>
        <template #left>
          <v-btn
            large
            @click.prevent="onReturnClicked">
            <v-icon>mdi-chevron-left</v-icon>
            戻る
          </v-btn>
        </template>
        <template #right>
          <v-btn
            v-if="!form.readonly"
            large
            color="default"
            class="mr-1"
            :disabled="!hasChanges"
            @click.prevent="onDraftSaveClicked">
            ブラウザに一時保存
          </v-btn>
          <v-btn
            v-if="!form.readonly"
            large
            color="primary"
            @click.prevent="onNextClicked">
            次へ
            <v-icon right>mdi-chevron-right</v-icon>
          </v-btn>
          <v-btn
            v-else
            large
            color="warning"
            @click.prevent="onFinishClicked">
            修了
            <v-icon right>mdi-exit-run</v-icon>
          </v-btn>
        </template>
      </global-footer>
    </v-card>
  </v-form>
</template>

<script>
import RoomTable from '../RoomTable'
import RoomControl from '../../mixins/RoomControl'
import {
  getStepSession, markAsUnsavedSession, setStepSession,
} from '../../logic/session'

export default {
  name: 'ConfirmationFormStep3',
  components: {
    RoomTable,
  },
  mixins: [RoomControl],
  props: {
    previousObject: {
      type: Object,
      default: () => {},
    },
    created: {
      type: Boolean,
      default: false,
    },
    applied: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isNew: {
        type: Boolean,
        default: false,
      },
      form: {
        rooms: [],
      },
      step1Form: {},
      error: {},
      hasChanges: false,
      noRoomsConfirmationIsShown: false,
    }
  },
  computed: {
    step1FormFloors() {
      const { floors } = this.step1Form
      return floors
    },
  },
  watch: {
    hasChanges() {
      this.$emit('hasChange', this.hasChanges)
    },
  },
  mounted() {
    this.loadItem()
  },
  methods: {
    loadItem() {
      this.step1Form = getStepSession('confirmation', 1)
      const form = getStepSession('confirmation', 3)
      if (form) {
        this.form = form
      }
      // 変更があればフラグをセット
      this.$watch('form', () => {
        this.hasChanges = true
      }, { deep: true })
    },
    async validateItem() {
      try {
        await window.axios.post('/api/confirmations/step3', this.form)
        this.error = {}
        return true
      } catch (e) {
        if (e.response.status === 422) {
          this.error = e.response.data.errors
        }
      }
      return false
    },
    onReturnClicked() {
      this.$emit('change', 2)
    },
    async onNextClicked() {
      if (this.form.readonly) {
        this.$emit('change', 4)
        return
      }
      if (this.form.rooms.length === 0) {
        this.noRoomsConfirmationIsShown = true
        return
      }
      if (await this.validateItem()) {
        if (this.hasChanges) {
          markAsUnsavedSession('confirmation')
        }
        setStepSession('confirmation', 3, this.form)
        this.$emit('change', 4)
      }
    },
    onFinishClicked() {
      this.$router.push({ name: 'index' })
    },
    nextRoomNo(floor) {
      const floors = {}
      this.form.rooms.forEach((item) => {
        floors[item.floors] = (floors[item.floors] ?? 0) + 1
      })
      const number = `${(floors[floor] ?? 0) + 1}`.padStart(3, '0')
      return floor > 0
        ? `F${floor} -${number}`
        : `B${Math.abs(floor)}-${number}`
    },
    onAddRowButtonClicked() {
      const lastRoom = this.form.rooms.length > 0
        ? this.form.rooms[this.form.rooms.length - 1]
        : { floors: 1 }
      this.form.rooms.push({
        room_no: this.nextRoomNo(lastRoom.floors),
        completion_year: lastRoom.completion_year ?? '1975',
        renovation_year1: lastRoom.renovation_year1 ?? null,
        renovation_year2: lastRoom.renovation_year2 ?? null,
        floors: lastRoom.floors,
        room_name: lastRoom.room_name ?? '',
      })
    },
    onDraftSaveClicked() {
      setStepSession('confirmation', 3, this.form)
      markAsUnsavedSession('confirmation')
      this.hasChanges = false
    },
    onConfirmationOkClicked() {
      if (this.hasChanges) {
        markAsUnsavedSession('confirmation')
      }
      setStepSession('confirmation', 3, this.form)
      this.$emit('change', 4)
      this.noRoomsConfirmationIsShown = false
    },
    onCloseDialog() {
      this.noRoomsConfirmationIsShown = false
    },
  },
}
</script>

<style scoped>
.delete-part {
  width: 20px;
  height: 20px;
}
</style>
